import { useEffect, useState } from 'react'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import type { PageProps } from 'gatsby'
import { graphql } from 'gatsby'
import type { ProductReviewPageQuery } from '@generated/graphql'
import { ReviewRedirectPage } from 'src/components/common/ReviewRedirectPage'
import type { ReviewProductPreview } from 'src/utils/reviews/fetchReviewProductPreview'
import { fetchReviewProductPreview } from 'src/utils/reviews/fetchReviewProductPreview'
import {
  getReviewUrl,
  hasValidReviewCode,
} from 'src/utils/reviews/getReviewUrl'

type ProductReviewPageProps = PageProps<ProductReviewPageQuery>

interface ServerDataProps {
  pageType: string
}

interface ServerDataResult {
  status: number
  props: ServerDataProps
}

function ProductReviewPage({
  location: { pathname },
  data: { site },
}: ProductReviewPageProps): JSX.Element {
  const [manufacturerCode, setManufacturerCode] = useState<
    string | null | undefined
  >(undefined)

  const [product, setProduct] = useState<ReviewProductPreview | null>(null)

  useEffect((): void => {
    const params = new URLSearchParams(window.location.search)

    setManufacturerCode(params.get('code'))
  }, [])

  useEffect(() => {
    if (!hasValidReviewCode(manufacturerCode)) {
      setProduct(null)

      return
    }

    let cancelled = false

    fetchReviewProductPreview(manufacturerCode).then((preview) => {
      if (!cancelled) {
        setProduct(preview)
      }
    })

    return () => {
      cancelled = true
    }
  }, [manufacturerCode])

  const hasResolvedManufacturerCode = manufacturerCode !== undefined
  const reviewUrl = hasValidReviewCode(manufacturerCode)
    ? getReviewUrl(manufacturerCode)
    : undefined

  return (
    <>
      <GatsbySeo
        title="Avalie o produto"
        titleTemplate={site?.siteMetadata?.titleTemplate ?? ''}
        canonical={`${site?.siteMetadata?.siteUrl}${pathname}`}
        openGraph={{
          title: 'Avalie o produto',
          images: [{ url: site?.siteMetadata?.ogImage ?? '' }],
        }}
      />

      {hasResolvedManufacturerCode ? (
        <ReviewRedirectPage reviewUrl={reviewUrl} product={product} />
      ) : null}
    </>
  )
}

/**
 * This query is run during SSG
 * */
export const querySSG = graphql`
  query ProductReviewPage {
    site {
      ...SiteInformation
    }
  }
`

export const getServerData = async (): Promise<ServerDataResult> => {
  return {
    status: 200,
    props: {
      pageType: 'Avalie o produto',
    },
  }
}

export default ProductReviewPage
